import { useDisclosure } from "@heroui/react";
import { ModalBuilder, ModalError, ModalField } from "./../modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import { getIdFromCategoryHandbook } from "@/utils/getIdFromHandbooks.ts";
import { Ticket, TicketStatus, TicketPriority } from "@/api/tickets/tickets.ts";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { CodependentCategoryHandbook } from "../../handbooks/categoryHandbook";

interface UpdateUserToLandlordTicketForm {
  priority: TicketPriority;
  status: TicketStatus;
}

interface UpdateToPlatformTicketForm {
  subject: string;
  category: number;
}

const updateUserToLandlordTicketFields: ModalField[] = [
  {
    label: "Приоритет",
    name: "priority",
    placeholder: "",
    type: "priority",
    isRequired: true,
  },
  {
    label: "Статус",
    name: "status",
    placeholder: "",
    type: "status",
    isRequired: true,
  },
];

const updateToPlatformTicketFields: ModalField[] = [
  {
    label: "hid",
    name: "to_platform",
    placeholder: "-",
    type: "hidden",
  },
  {
    label: "Тема обращения",
    name: "subject",
    placeholder: "Введите тему обращения",
    type: "text",
    isRequired: true,
  },
  {
    label: "Категория",
    name: "category",
    placeholder: "",
    type: "codependent",
    deps: ["to_platform"],
    element: CodependentCategoryHandbook,
    isRequired: true,
  },
];

export function EditTicketModal({
  disclosure,
  ticket,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  ticket: Ticket | null;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);

  const handleError = useErrorHandling();

  const isUserToLandlord = ticket?.type === "user_to_landlord";
  const fields = isUserToLandlord
    ? updateUserToLandlordTicketFields
    : updateToPlatformTicketFields;

  const onSubmit = (
    data: UpdateUserToLandlordTicketForm | UpdateToPlatformTicketForm,
  ) => {
    return new Promise<boolean>((resolve) => {
      if (isUserToLandlord) {
        const userToLandlordData = data as UpdateUserToLandlordTicketForm;
        api.tickets
          .update("user_to_landlord", ticket!.id, {
            priority: userToLandlordData.priority,
            status: userToLandlordData.status,
          })
          .then(() => {
            loadHandbooks({ handbooks: ["ticketCategories"] });
            disclosure.onClose();
            toast.success("Тикет успешно обновлен");
            resolve(true);
          })
          .catch((err) => {
            console.log(err);
            const { errorMessage, parsedErrors } = handleError(err);
            setModalErrors(parsedErrors || []);
            toast.error(errorMessage);
            resolve(false);
          });
      } else {
        const toPlatformData = data as UpdateToPlatformTicketForm;
        getIdFromCategoryHandbook(String(toPlatformData.category))
          .then((category) => {
            return api.tickets.update("to_platform", ticket!.id, {
              subject: toPlatformData.subject,
              category: Number(category),
            });
          })
          .then(() => {
            loadHandbooks({ handbooks: ["ticketCategories"] });
            disclosure.onClose();
            toast.success("Тикет успешно обновлен");
            resolve(true);
          })
          .catch((err) => {
            console.log(err);
            const { errorMessage, parsedErrors } = handleError(err);
            setModalErrors(parsedErrors || []);
            toast.error(errorMessage);
            resolve(false);
          });
      }
    });
  };

  const getDefaultValues = () => {
    if (isUserToLandlord) {
      return {
        priority: ticket?.priority,
        status: ticket?.status,
      };
    } else {
      return {
        to_platform: "to_platform",
        subject: ticket?.subject,
        category: ticket?.category,
      };
    }
  };

  return (
    <ModalBuilder
      title={`Редактирование тикета ${isUserToLandlord ? "к арендодателю" : "к платформе"}`}
      fields={fields}
      isOpen={isOpen}
      isSensitiveData={true}
      onOpenChange={onOpenChange}
      modalErrors={modalErrors}
      onSubmit={onSubmit}
      submitButtonText="Сохранить"
      defaultValues={getDefaultValues()}
    />
  );
}
